import React, { useRef, useState, useEffect } from 'react';
import './CaseStudies.css';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import globalSupplyChainImg from '../assets/Global_supply_chain_ ERP.webp';
import databaseMigrationImg from '../assets/Database_migrationjpg.webp';
import legalContractImg from '../assets/legal contract.webp';

interface CaseStudiesProps {
  preview?: boolean;
}

// Category pill color config
const categoryConfig: Record<string, { bg: string; color: string; accent: string }> = {
  'Enterprise ERP': { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', accent: '#8b5cf6' },
  'AI & Diagnostics': { bg: 'rgba(20,184,166,0.15)', color: '#2dd4bf', accent: '#14b8a6' },
  'Industrial Automation': { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', accent: '#f59e0b' },
  'Mobile Applications': { bg: 'rgba(14,165,233,0.15)', color: '#38bdf8', accent: '#0ea5e9' },
  'Cloud Architecture': { bg: 'rgba(99,102,241,0.15)', color: '#818cf8', accent: '#6366f1' },
  'Workflow Automation': { bg: 'rgba(244,63,94,0.15)', color: '#fb7185', accent: '#f43f5e' },
};

// Animated counter that counts up from 0 to a numeric target
const CountUp: React.FC<{ value: string; isVisible: boolean }> = ({ value, isVisible }) => {
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!isVisible) return;

    const match = value.match(/^([+\-]?)(\d+(?:\.\d+)?)(.*)/);
    if (!match) { setDisplay(value); return; }

    const prefix = match[1];
    const target = parseFloat(match[2]);
    const suffix = match[3];

    let start = 0;
    const duration = 1200; // ms
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutQuad
      const eased = progress * (2 - progress);
      const current = start + eased * (target - start);

      if (Number.isInteger(target)) {
        setDisplay(`${prefix}${Math.floor(current)}${suffix}`);
      } else {
        setDisplay(`${prefix}${current.toFixed(1)}${suffix}`);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, isVisible]);

  return <span>{display}</span>;
};

interface CaseCardProps {
  cs: {
    category: string;
    title: string;
    desc: string;
    metrics: { label: string; value: string }[];
    link: string;
  };
  idx: number;
}

const caseImages = [
  globalSupplyChainImg, // Global Supply Chain ERP Migration
  'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80', // AI & Diagnostics
  'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80', // Industrial Automation
  databaseMigrationImg, // High-Availability Database Migration
  legalContractImg, // Corporate Legal Contract Generator
];

const CaseCard: React.FC<CaseCardProps> = ({ cs, idx }) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const imageUrl = caseImages[idx] || caseImages[0];
  const cfg = categoryConfig[cs.category] ?? { bg: 'rgba(255,255,255,0.08)', color: '#a1a1aa', accent: '#71717a' };

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setIsVisible(true); io.disconnect(); }
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Link
      ref={cardRef}
      to={cs.link}
      className={`case-card-stacked ${isVisible ? 'active' : ''}`}
      style={{
        '--card-index': idx,
        '--card-accent': cfg.accent,
        '--card-color': cfg.color,
        '--card-bg': cfg.bg,
        transitionDelay: `${idx * 0.05}s`,
      } as React.CSSProperties}
      aria-label={`View case studies: ${cs.title}`}
    >
      {/* Left Column: Image wrapper */}
      <div className="case-card-image-section">
        <img loading="lazy" src={imageUrl} alt={cs.title} className="case-card-img"  title="Vivify Soft Tech" />
      </div>

      {/* Right Column: Content Section */}
      <div className="case-card-info-section">
        <div className="case-card-header-line">
          <div className="case-card-header-inner">
            <h3 className="case-card-title">{cs.title}</h3>
            <span className="case-card-category" style={{ color: cfg.color }}>{cs.category}</span>
          </div>
        </div>

        <p className="case-card-desc">{cs.desc}</p>

        {/* Dynamic Metric Badges */}
        <div className="case-card-metrics-row">
          {cs.metrics.map((met, mIdx) => (
            <div key={mIdx} className="metric-badge-box">
              <span className="metric-badge-value">
                <CountUp value={met.value} isVisible={isVisible} />
              </span>
              <span className="metric-badge-label">{met.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
};

export const CaseStudies: React.FC<CaseStudiesProps> = ({ preview = false }) => {
  const caseList = [
    {
      category: 'Enterprise ERP',
      title: 'Global Supply Chain ERP Migration',
      desc: 'We migrated a legacy ERP system to a modern enterprise platform, improving supply chain visibility, operational efficiency, and multi-region business performance.',
      metrics: [
        { label: 'Operational Efficiency', value: '+120%' },
        { label: 'Successful Migration', value: '6 Months' }
      ],
      link: '/case-studies/erp-transformation-japan-manufacturing',
    },
    {
      category: 'AI & Diagnostics',
      title: 'Neural Dental Diagnostic Suite Deployment',
      desc: 'Implemented an AI-powered dental diagnostic platform that analyzes X-ray images to detect cavities, identify oral conditions, and assist dentists with faster, more accurate clinical decisions.',
      metrics: [
        { label: 'Clinical Accuracy', value: '99.4%' },
        { label: 'Diagnostics Completed', value: '500K+' }
      ],
      link: '/case-studies/ai-powered-dental-diagnostics-chennai',
    },
    {
      category: 'Industrial Automation',
      title: 'Smart Manufacturing & Industrial IoT',
      desc: 'Built an intelligent manufacturing solution that captures real-time machine data, tracks production performance, and enables predictive maintenance for uninterrupted operations.',
      metrics: [
        { label: 'Operational Performance', value: '+18%' },
        { label: 'Maintenance Downtime', value: '-40%' }
      ],
      link: '/case-studies/industrial-automation-tamil-nadu',
    },
    {
      category: 'Cloud Architecture',
      title: 'High-Availability Cloud Database Migration',
      desc: 'Migrated enterprise databases to a highly available cloud platform with automated failover, real-time replication, and zero downtime, ensuring secure, reliable, and scalable data operations.',
      metrics: [
        { label: 'Infrastructure Costs', value: '-35%' },
        { label: 'System Downtime', value: '0%' }
      ],
      link: '/case-studies/scalable-cloud-architecture',
    },
    {
      category: 'Workflow Automation',
      title: 'Intelligent Legal Contract Automation',
      desc: 'Automated contract creation, compliance validation, and approval workflows with an AI-powered platform that reduced manual effort and accelerated document processing.',
      metrics: [
        { label: 'Time Saved Weekly', value: '40h+' },
        { label: 'Contract Generation', value: '10x Faster' }
      ],
      link: '/case-studies/seamless-workflow-automation',
    }
  ];

  const displayList = preview ? caseList.slice(0, 3) : caseList;

  return (
    <section className="section case-studies-section" id="case-studies">
      <div className="container">
        <div className="section-header centered">
          <h2 className="section-title">Success Stories That Drive Innovation</h2>
          <p className="section-subtitle">Every solution we create is designed with purpose. Explore the journeys behind our most impactful projects and see how innovative solutions deliver measurable business results.</p>
        </div>

        <div className="case-studies-stacked-list">
          {displayList.map((cs, idx) => (
            <CaseCard
              key={idx}
              cs={cs}
              idx={idx}
            />
          ))}
        </div>

        {preview && (
          <div className="centered" style={{ marginTop: '4rem' }}>
            <Link to="/case-studies">
              <Button variant="primary" size="md" icon={<ArrowRight size={16} />}>
                View All Case Studies
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudies;
